import { queryClient } from 'App';
import { getRecoil, setRecoil } from 'recoil-nexus';

import { iaRa } from 'lib/helpers/utility';
import { ELIGIBILITY_CHECKS_KEY } from 'lib/hooks/queries/billing/useIncomeIQueueEligibilityChecks';

import { openDock } from 'components/Dock/lib/useDock';
import { DOCK_KINDS } from 'components/Dock/lib/dockKinds';
import {
  isRunDone,
  isTerminalStatus
} from 'components/practice/Eligibility/BatchEligibility/components/EligibilityDockCard/lib/helpers';
import { batchEligibilityProgressState } from 'components/practice/Eligibility/BatchEligibility/components/EligibilityDockCard/lib/batchEligibilityProgressState';

// Live batch-eligibility progress from the async worker (emitters/eligibility/
// batchProgress.js). Upserts the run into the tracker atom and ensures the
// bottom-dock card is open; on a terminal status it also refreshes the History
// list so its final summary matches.
export default (data) => {
  const { batchRunId, status, total, completed, successfulProfiles, failedProfiles } = data || {};
  if (!batchRunId) return;

  const prev = getRecoil(batchEligibilityProgressState);
  // Staff dismissed this run's card; later ticks must not re-open it.
  if (iaRa(prev.dismissed).some((id) => String(id) === String(batchRunId))) return;

  const prevRun = prev.runs?.[batchRunId] || {};
  const wasTerminal = isTerminalStatus(prevRun.status);

  // A run only moves forward: ignore a stale non-terminal tick that arrives after a
  // terminal one (emits aren't ordered), else the card would never auto-dismiss.
  if (wasTerminal && !isTerminalStatus(status)) return;

  const nextTotal = total ?? prevRun.total ?? 0;
  const nextCompleted = completed ?? prevRun.completed ?? 0;

  setRecoil(batchEligibilityProgressState, {
    ...prev,
    runs: {
      ...prev.runs,
      [batchRunId]: {
        batchRunId,
        status,
        total: nextTotal,
        completed: nextCompleted,
        successfulProfiles: successfulProfiles ?? prevRun.successfulProfiles ?? 0,
        failedProfiles: failedProfiles ?? prevRun.failedProfiles ?? 0
      }
    }
  });

  openDock({ id: 'eligibility-batch', kind: DOCK_KINDS.ELIGIBILITY, side: 'right' });

  // Refresh History on the first transition to done, by EITHER signal: terminal status tick or
  // counts reaching total. The terminal emit is best-effort, so the count signal is the fallback.
  const becameTerminal = isTerminalStatus(status) && !wasTerminal;
  const becameDone =
    isRunDone({ status, total: nextTotal, completed: nextCompleted }) && !isRunDone(prevRun);

  if (becameTerminal || becameDone) {
    queryClient.invalidateQueries(['batch-eligibility-runs']);
    queryClient.invalidateQueries([ELIGIBILITY_CHECKS_KEY]);
    // Responses are read off the patient query, which the batch doesn't update. Mark stale only:
    // refetching an open patient would reset forms built on it (enableReinitialize).
    queryClient.invalidateQueries(['patient'], { refetchType: 'none' });
  }
};
